import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import TransportMapLibre from './TransportMapLibre';
import InsufficientFiresModal from './InsufficientFiresModal';
import TransportSavedLocationsModal from './TransportSavedLocationsModal';
import { getBackendErrorMessage } from '../utils/toastError';
import { extractCoordsFromText, resolveMapLink, extractPlaceNameFromUrl, searchLocationSuggestions } from '../utils/transportHelper';
import toast from 'react-hot-toast';
import { getQrDataUrlForUrl, downloadQrForUrl } from '../utils/qr';

const MX_TRANSPORT_FROM_SUGGESTIONS_KEY = 'mx_transport_from_suggestions';
const MX_TRANSPORT_TO_SUGGESTIONS_KEY = 'mx_transport_to_suggestions';
const MX_TRANSPORT_LAST_FROM_KEY = 'mx_transport_last_from_location';
const MX_TRANSPORT_LAST_TO_KEY = 'mx_transport_last_to_location';
const MX_TRANSPORT_QUOTE_CACHE_KEY = 'mx_transport_quote_cache_v1';

const MAPTILER_KEY = process.env.REACT_APP_MAPTILER_KEY;

function loadStringSuggestions(key) {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map((v) => String(v || '').trim())
      .filter(Boolean)
      .slice(0, 10);
  } catch {
    return [];
  }
}

function writeStringSuggestions(key, list) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(key, JSON.stringify(Array.isArray(list) ? list : []));
  } catch {
  }
}

function coordsEqual(a, b, eps = 1e-6) {
  if (!a || !b) return false;
  const latA = Number(a.lat);
  const lngA = Number(a.lng);
  const latB = Number(b.lat);
  const lngB = Number(b.lng);
  if (!Number.isFinite(latA) || !Number.isFinite(lngA) || !Number.isFinite(latB) || !Number.isFinite(lngB)) return false;
  return Math.abs(latA - latB) <= eps && Math.abs(lngA - lngB) <= eps;
}

function makeQuoteCacheKey(fromCoords, toCoords) {
  if (!fromCoords || !toCoords) return null;
  if (!Number.isFinite(Number(fromCoords.lat)) || !Number.isFinite(Number(fromCoords.lng))) return null;
  if (!Number.isFinite(Number(toCoords.lat)) || !Number.isFinite(Number(toCoords.lng))) return null;
  return `${Number(fromCoords.lat).toFixed(6)},${Number(fromCoords.lng).toFixed(6)}|${Number(toCoords.lat).toFixed(6)},${Number(toCoords.lng).toFixed(6)}`;
}

function normalizePlaceTypeLabel(result) {
  const key = String(result?.categoryKey || '').toLowerCase();
  if (key.includes('address') || key.includes('street')) return 'Dirección';
  if (key.includes('place') || key.includes('locality') || key.includes('neighbourhood')) return 'Zona';
  if (key.includes('poi') || key.includes('venue')) return 'Lugar';
  if (key.includes('station') || key.includes('terminal')) return 'Terminal';
  if (key.includes('shop') || key.includes('store') || key.includes('commercial')) return 'Comercio';
  if (key.includes('building')) return 'Edificio';
  return 'Ubicación';
}

function getPlaceTypeIcon(result) {
  const key = String(result?.categoryKey || '').toLowerCase();
  if (result?.isCurrentLocation) return '📍';
  if (key.includes('address') || key.includes('street')) return '🛣️';
  if (key.includes('station') || key.includes('terminal')) return '🚌';
  if (key.includes('shop') || key.includes('store') || key.includes('commercial')) return '🏪';
  if (key.includes('building')) return '🏢';
  if (key.includes('place') || key.includes('locality') || key.includes('neighbourhood')) return '📌';
  return '📍';
}

function safeNum(v, fallback = null) {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

function readQuoteCache() {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(MX_TRANSPORT_QUOTE_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeQuoteCache(payload) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(MX_TRANSPORT_QUOTE_CACHE_KEY, JSON.stringify(payload || null));
  } catch {
  }
}

async function reverseGeocode(coords) {
  if (!MAPTILER_KEY) return null;
  const lat = coords?.lat;
  const lng = coords?.lng;
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;

  try {
    const url = `https://api.maptiler.com/geocoding/${encodeURIComponent(String(lng))},${encodeURIComponent(
      String(lat)
    )}.json?key=${encodeURIComponent(MAPTILER_KEY)}`;
    const res = await fetch(url);
    if (!res.ok) return null;
    const data = await res.json();
    const feature = Array.isArray(data?.features) ? data.features[0] : null;
    const place = feature?.place_name || feature?.text || null;
    return place ? String(place).trim() : null;
  } catch {
    return null;
  }
}

async function normalizeMapLink(working) {
  const res = await resolveMapLink(working);
  if (!res) return null;
  return {
    url: res.link || working,
    coords: res.coords || null,
    place_name: res.place_name || null
  };
}

function SectionCard({ title, children }) {
  return (
    <div className="bg-[#0b1220] border border-slate-800 rounded-xl p-4 shadow-lg shadow-black/30">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-slate-100 font-semibold text-base">{title}</h3>
      </div>
      {children}
    </div>
  );
}

export default function RideCreateForm({ onCreated, onQuoteActiveChange, title = 'Crear traslado de persona', compact = false, autoLocateOnOpenToken = 0, storeId = null }) {
  const queryClient = useQueryClient();
  const fromTouchedRef = useRef(false);
  const lastQuoteActiveRef = useRef(false);
  const autoQuoteTimerRef = useRef(null);
  const fromResolveTimerRef = useRef(null);
  const toResolveTimerRef = useRef(null);
  const fromResolvingRef = useRef(false);
  const toResolvingRef = useRef(false);
  const fromResolveSeqRef = useRef(0);
  const toResolveSeqRef = useRef(0);
  const fromCoordsRef = useRef(null);
  const toCoordsRef = useRef(null);
  const searchTimerRef = useRef({ from: null, to: null });
  const searchSeqRef = useRef({ from: 0, to: 0 });
  const originAutoLocateSeqRef = useRef(0);
  const inFlightOriginAutoLocateRef = useRef(false);
  const lastConsumedAutoLocateTokenRef = useRef(0);
  const quoteRef = useRef(null);
  const quoteLockedRef = useRef(false);
  const explicitClearRef = useRef(false);
  const lastQuotedKeyRef = useRef(null);
  const [fromSuggestions, setFromSuggestions] = useState(() => loadStringSuggestions(MX_TRANSPORT_FROM_SUGGESTIONS_KEY));
  const [toSuggestions, setToSuggestions] = useState(() => loadStringSuggestions(MX_TRANSPORT_TO_SUGGESTIONS_KEY));
  const [recentFromSelection, setRecentFromSelection] = useState('');
  const [recentToSelection, setRecentToSelection] = useState('');

  const { data: fiatContext } = useQuery({
    queryKey: ['fiat-context'],
    queryFn: async () => {
      const response = await axios.get('/api/economy/fiat-context');
      return response.data;
    },
    staleTime: 60 * 1000
  });

  const [fromLabel, setFromLabel] = useState('');
  const [toLabel, setToLabel] = useState('');
  const [fromCoords, setFromCoords] = useState(null);
  const [toCoords, setToCoords] = useState(null);
  const [vehicleType, setVehicleType] = useState('moto');
  const [notes, setNotes] = useState('');
  const [quote, setQuote] = useState(null);
  const [selectedRouteIndex, setSelectedRouteIndex] = useState(0);
  const [isQuoting, setIsQuoting] = useState(false);
  const [pickMode, setPickMode] = useState(null);
  const [googlePickerOpen, setGooglePickerOpen] = useState(false);
  const [googlePickerTarget, setGooglePickerTarget] = useState(null);
  const [googlePickerText, setGooglePickerText] = useState('');
  const [googlePickerError, setGooglePickerError] = useState(null);
  const [googlePickerCoords, setGooglePickerCoords] = useState(null);
  const [googlePickerResolving, setGooglePickerResolving] = useState(false);
  const [error, setError] = useState(null);
  const [showInsufficientFires, setShowInsufficientFires] = useState(false);
  const [missingFires, setMissingFires] = useState(0);
  const [savedLocationsOpen, setSavedLocationsOpen] = useState(false);
  const [savedLocationsTarget, setSavedLocationsTarget] = useState('from');

  const [invoiceOpen, setInvoiceOpen] = useState(false);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [invoiceError, setInvoiceError] = useState(null);
  const [invoiceData, setInvoiceData] = useState(null);
  const [invoiceQrOpen, setInvoiceQrOpen] = useState(false);
  const [isLocatingOrigin, setIsLocatingOrigin] = useState(false);
  const [originLocateError, setOriginLocateError] = useState(null);
  const [fromSearchResults, setFromSearchResults] = useState([]);
  const [toSearchResults, setToSearchResults] = useState([]);
  const [fromSearchOpen, setFromSearchOpen] = useState(false);
  const [toSearchOpen, setToSearchOpen] = useState(false);
  const [fromSearchLoading, setFromSearchLoading] = useState(false);
  const [toSearchLoading, setToSearchLoading] = useState(false);

  const buildLocationPayload = (label, coords) => {
    const parsed = coords && coords.lat != null && coords.lng != null ? coords : extractCoordsFromText(label);
    const payload = { label, address: label };
    if (parsed) payload.coords = { lat: Number(parsed.lat), lng: Number(parsed.lng) };
    return payload;
  };

  const copyInvoiceLink = async (url) => {
    try {
      if (!url) return;
      await navigator.clipboard.writeText(url);
      toast.success('Link copiado');
    } catch {
      try {
        const ok = window.prompt('Copia este link:', url);
        if (ok !== null) toast.success('Listo');
      } catch {
        toast.error('No se pudo copiar el link');
      }
    }
  };

  const shareInvoiceLink = async (url) => {
    try {
      if (!url) return;
      if (navigator.share) {
        await navigator.share({ title: 'Pago de Transporte', text: 'Paga el transporte aquí:', url });
        return;
      }
      await copyInvoiceLink(url);
    } catch {
      await copyInvoiceLink(url);
    }
  };

  const generateInvoiceFromQuote = async ({ resQuote, routeIndexOverride } = {}) => {
    setInvoiceError(null);
    setInvoiceLoading(true);
    setInvoiceData(null);
    try {
      const payload = {
        kind: 'ride',
        from_location: buildLocationPayload(fromLabel, fromCoords),
        to_location: buildLocationPayload(toLabel, toCoords),
        vehicle_type: vehicleType,
        notes,
        costing: resQuote?.costing || quote?.costing || null,
        subtotal_usdt: resQuote?.subtotal_usdt || quote?.subtotal_usdt || null,
        route_index: Number.isFinite(Number(routeIndexOverride)) ? Number(routeIndexOverride) : selectedRouteIndex,
        max_alternatives: 3,
        expires_in_seconds: 900,
      };

      const res = await axios.post('/api/transport/quote/payment/start', payload);
      const sessionId = res.data?.session_id;
      if (!sessionId) throw new Error('No se pudo generar session_id');

      const url = `${window.location.origin}/transport/pay/${sessionId}`;
      const qrDataUrl = await getQrDataUrlForUrl(url);
      setInvoiceData({ url, sessionId, expiresAt: res.data?.expires_at || null, qrDataUrl });
    } catch (err) {
      setInvoiceError(getBackendErrorMessage(err, 'No se pudo generar la factura'));
    } finally {
      setInvoiceLoading(false);
    }
  };

  const [travelMode, setTravelMode] = useState('now');
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');

  const clearQuote = (reason) => {
    if (!explicitClearRef.current) {
      return;
    }
    explicitClearRef.current = false;
    setQuote(null);
    setSelectedRouteIndex(0);
  };

  const applyFromLocation = ({ label, coords }) => {
    if (!coords || coords.lat == null || coords.lng == null) return;
    const parsed = { lat: Number(coords.lat), lng: Number(coords.lng) };
    if (!Number.isFinite(parsed.lat) || !Number.isFinite(parsed.lng)) return;

    fromTouchedRef.current = true;
    quoteLockedRef.current = false;

    const didCoordsChange = !coordsEqual(fromCoords, parsed);
    setFromCoords(parsed);
    setFromLabel(String(label || '').trim() || 'Origen');

    if (quote && didCoordsChange) {
      explicitClearRef.current = true;
      clearQuote('saved-from');
    }
  };

  const applyToLocation = ({ label, coords }) => {
    if (!coords || coords.lat == null || coords.lng == null) return;
    const parsed = { lat: Number(coords.lat), lng: Number(coords.lng) };
    if (!Number.isFinite(parsed.lat) || !Number.isFinite(parsed.lng)) return;

    quoteLockedRef.current = false;

    const didCoordsChange = !coordsEqual(toCoords, parsed);
    setToCoords(parsed);
    setToLabel(String(label || '').trim() || 'Destino');

    if (quote && didCoordsChange) {
      explicitClearRef.current = true;
      clearQuote('saved-to');
    }
  };

  const clearLocationSearch = React.useCallback((target) => {
    if (target === 'from') {
      setFromSearchResults([]);
      setFromSearchLoading(false);
      setFromSearchOpen(false);
      return;
    }
    setToSearchResults([]);
    setToSearchLoading(false);
    setToSearchOpen(false);
  }, []);

  const clearStoredSuggestions = React.useCallback((target) => {
    if (typeof window === 'undefined') return;
    if (target === 'from') {
      window.localStorage.removeItem(MX_TRANSPORT_FROM_SUGGESTIONS_KEY);
      setFromSuggestions([]);
      setRecentFromSelection('');
      return;
    }
    window.localStorage.removeItem(MX_TRANSPORT_TO_SUGGESTIONS_KEY);
    setToSuggestions([]);
    setRecentToSelection('');
  }, []);

  const currentOriginSuggestion = useMemo(() => {
    if (!fromCoords || !Number.isFinite(Number(fromCoords.lat)) || !Number.isFinite(Number(fromCoords.lng))) return null;
    return {
      id: `current-origin:${Number(fromCoords.lat).toFixed(6)},${Number(fromCoords.lng).toFixed(6)}`,
      label: String(fromLabel || '').trim() || 'Mi ubicación actual',
      shortLabel: 'Mi ubicación actual',
      coords: { lat: Number(fromCoords.lat), lng: Number(fromCoords.lng) },
      categoryKey: 'current_location',
      featureType: 'current_location',
      placeType: ['current_location'],
      context: [],
      isCurrentLocation: true,
    };
  }, [fromCoords, fromLabel]);

  const decoratedFromSearchResults = useMemo(() => {
    const base = Array.isArray(fromSearchResults) ? fromSearchResults : [];
    if (!currentOriginSuggestion) return base;
    const filtered = base.filter((item) => String(item?.id || '') !== String(currentOriginSuggestion.id));
    return [currentOriginSuggestion, ...filtered];
  }, [currentOriginSuggestion, fromSearchResults]);

  const localSearchContext = useMemo(() => {
    return {
      from: [fromLabel, currentOriginSuggestion?.label, ...(currentOriginSuggestion?.context || [])].filter(Boolean).join(' '),
      to: [toLabel, fromLabel, currentOriginSuggestion?.label, ...(currentOriginSuggestion?.context || [])].filter(Boolean).join(' '),
    };
  }, [fromLabel, toLabel, currentOriginSuggestion]);

  const scheduleLocationSearch = React.useCallback(
    (target, rawValue) => {
      const next = String(rawValue || '').trim();
      const setLoading = target === 'from' ? setFromSearchLoading : setToSearchLoading;
      const setResults = target === 'from' ? setFromSearchResults : setToSearchResults;
      const setOpen = target === 'from' ? setFromSearchOpen : setToSearchOpen;

      if (searchTimerRef.current[target]) {
        clearTimeout(searchTimerRef.current[target]);
        searchTimerRef.current[target] = null;
      }

      if (!next || next.length < 3 || extractCoordsFromText(next) || /^https?:\/\//i.test(next)) {
        clearLocationSearch(target);
        return;
      }

      searchSeqRef.current[target] += 1;
      const seq = searchSeqRef.current[target];
      setLoading(true);
      setOpen(true);

      searchTimerRef.current[target] = setTimeout(async () => {
        const proximity =
          target === 'from'
            ? fromCoordsRef.current || toCoordsRef.current || null
            : toCoordsRef.current || fromCoordsRef.current || null;
        const results = await searchLocationSuggestions(next, {
          proximity,
          limit: 6,
          localContextText: target === 'from' ? localSearchContext.from : localSearchContext.to,
        });
        if (searchSeqRef.current[target] !== seq) return;
        setResults(results);
        setLoading(false);
        setOpen(target === 'from' ? (results.length > 0 || !!currentOriginSuggestion) : results.length > 0);
      }, 280);
    },
    [clearLocationSearch, currentOriginSuggestion, localSearchContext]
  );

  const selectLocationSearchResult = React.useCallback(
    (target, result) => {
      if (!result?.coords) return;
      if (target === 'from') {
        applyFromLocation({ label: result.label, coords: result.coords });
        clearLocationSearch('from');
        return;
      }
      applyToLocation({ label: result.label, coords: result.coords });
      clearLocationSearch('to');
    },
    [applyFromLocation, applyToLocation, clearLocationSearch]
  );

  useEffect(() => {
    fromCoordsRef.current = fromCoords;
  }, [fromCoords]);

  useEffect(() => {
    toCoordsRef.current = toCoords;
  }, [toCoords]);

  useEffect(() => {
    quoteRef.current = quote;
    quoteLockedRef.current = !!quote;
  }, [quote]);

  useEffect(() => {
    return () => {
      if (searchTimerRef.current.from) clearTimeout(searchTimerRef.current.from);
      if (searchTimerRef.current.to) clearTimeout(searchTimerRef.current.to);
    };
  }, []);

  useEffect(() => {
    if (quote) return;
    if (isQuoting) return;
    if (!fromCoords || !toCoords) return;

    const key = makeQuoteCacheKey(fromCoords, toCoords);
    if (!key) return;

    const cached = readQuoteCache();
    if (!cached || cached.key !== key) return;
    const ts = Number(cached.ts);
    if (!Number.isFinite(ts)) return;
    if (Date.now() - ts > 10 * 60 * 1000) return;
    if (!cached.quote) return;

    setQuote(cached.quote);
    if (Number.isFinite(Number(cached.selectedRouteIndex))) {
      setSelectedRouteIndex(Number(cached.selectedRouteIndex));
    }
  }, [fromCoords, toCoords, quote, isQuoting]);

  useEffect(() => {
    const quoteActive = !!quote;
    if (lastQuoteActiveRef.current === quoteActive) return;
    lastQuoteActiveRef.current = quoteActive;
    try {
      if (typeof onQuoteActiveChange === 'function') {
        onQuoteActiveChange(quoteActive);
      }
    } catch {
    }
    return () => {
      try {
        if (typeof onQuoteActiveChange === 'function') {
          onQuoteActiveChange(false);
        }
      } catch {
      }
    };
  }, [quote, onQuoteActiveChange]);

  const requestImmediateCurrentLocation = React.useCallback(async ({ prioritizeCurrentLocation = false } = {}) => {
    if (!navigator.geolocation) {
      setOriginLocateError('Tu dispositivo no soporta geolocalización.');
      setIsLocatingOrigin(false);
      return;
    }
    if (inFlightOriginAutoLocateRef.current) return;

    originAutoLocateSeqRef.current += 1;
    const seq = originAutoLocateSeqRef.current;
    inFlightOriginAutoLocateRef.current = true;
    setIsLocatingOrigin(true);
    setOriginLocateError(null);

    if (prioritizeCurrentLocation) {
      quoteLockedRef.current = false;
      setFromCoords(null);
      setFromLabel('Ubicándote...');
    }

    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        if (originAutoLocateSeqRef.current !== seq) return;
        if (fromTouchedRef.current && !prioritizeCurrentLocation) return;
        if (quoteLockedRef.current && !prioritizeCurrentLocation) return;

        const coords = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        setFromCoords(coords);
        setFromLabel('Mi ubicación actual');

        const place = await reverseGeocode(coords);
        if (originAutoLocateSeqRef.current !== seq) return;
        if (fromTouchedRef.current && !prioritizeCurrentLocation) return;
        if (quoteLockedRef.current && !prioritizeCurrentLocation) return;

        setFromLabel(place || 'Mi ubicación actual');
        setIsLocatingOrigin(false);
        setOriginLocateError(null);
        inFlightOriginAutoLocateRef.current = false;
      },
      () => {
        if (originAutoLocateSeqRef.current !== seq) return;
        if (prioritizeCurrentLocation) {
          setFromLabel('');
          setFromCoords(null);
        }
        setIsLocatingOrigin(false);
        setOriginLocateError('No se pudo detectar tu ubicación actual. Puedes escribir el origen o seleccionarlo en el mapa.');
        inFlightOriginAutoLocateRef.current = false;
      },
      { enableHighAccuracy: true, maximumAge: 30000, timeout: 12000 }
    );
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    if (!toCoords && !toLabel) {
      try {
        const rawTo = window.localStorage.getItem(MX_TRANSPORT_LAST_TO_KEY);
        if (rawTo) {
          const parsed = JSON.parse(rawTo);
          if (parsed?.coords?.lat != null && parsed?.coords?.lng != null) {
            const coords = { lat: Number(parsed.coords.lat), lng: Number(parsed.coords.lng) };
            if (Number.isFinite(coords.lat) && Number.isFinite(coords.lng)) {
              setToCoords(coords);
              setToLabel(String(parsed.label || parsed.address || '').trim() || 'Destino');
            }
          }
        }
      } catch {
      }
    }

    if (autoLocateOnOpenToken) return;
    if (fromCoords || fromLabel) return;

    try {
      const rawFrom = window.localStorage.getItem(MX_TRANSPORT_LAST_FROM_KEY);
      if (rawFrom) {
        const parsed = JSON.parse(rawFrom);
        if (parsed?.coords?.lat != null && parsed?.coords?.lng != null) {
          const coords = { lat: Number(parsed.coords.lat), lng: Number(parsed.coords.lng) };
          if (Number.isFinite(coords.lat) && Number.isFinite(coords.lng)) {
            setFromCoords(coords);
            setFromLabel(String(parsed.label || parsed.address || '').trim() || 'Origen');
            return;
          }
        }
      }
    } catch {
    }
  }, [fromCoords, fromLabel, autoLocateOnOpenToken]);

  useEffect(() => {
    if (autoLocateOnOpenToken) return;
    if (!navigator.geolocation) return;
    if (fromTouchedRef.current) return;
    if (quoteLockedRef.current) return;

    requestImmediateCurrentLocation({ prioritizeCurrentLocation: false });
  }, [autoLocateOnOpenToken, requestImmediateCurrentLocation]);

  useEffect(() => {
    if (!autoLocateOnOpenToken) return;
    if (lastConsumedAutoLocateTokenRef.current === autoLocateOnOpenToken) return;
    lastConsumedAutoLocateTokenRef.current = autoLocateOnOpenToken;
    fromTouchedRef.current = false;
    requestImmediateCurrentLocation({ prioritizeCurrentLocation: true });
  }, [autoLocateOnOpenToken, requestImmediateCurrentLocation]);

  const quoteFromCoords =
    quote?.from?.coords && quote.from.coords.lat != null && quote.from.coords.lng != null
      ? { lat: Number(quote.from.coords.lat), lng: Number(quote.from.coords.lng) }
      : null;
  const quoteToCoords =
    quote?.to?.coords && quote.to.coords.lat != null && quote.to.coords.lng != null
      ? { lat: Number(quote.to.coords.lat), lng: Number(quote.to.coords.lng) }
      : null;

  const quoteHasFrom = quoteFromCoords && Number.isFinite(quoteFromCoords.lat) && Number.isFinite(quoteFromCoords.lng);
  const quoteHasTo = quoteToCoords && Number.isFinite(quoteToCoords.lat) && Number.isFinite(quoteToCoords.lng);

  const quoteRoutes = useMemo(() => (Array.isArray(quote?.routes) ? quote.routes : []), [quote]);

  const bestRouteIndex = useMemo(() => {
    if (!quoteRoutes.length) return 0;
    let bestIdx = 0;
    let bestDistance = Number(quoteRoutes[0]?.distance_km);
    if (!Number.isFinite(bestDistance)) bestDistance = Infinity;
    quoteRoutes.forEach((r, idx) => {
      const d = Number(r?.distance_km);
      if (Number.isFinite(d) && d < bestDistance) {
        bestDistance = d;
        bestIdx = idx;
      }
    });
    return bestIdx;
  }, [quoteRoutes]);

  useEffect(() => {
    if (!quoteRoutes.length) return;
    if (selectedRouteIndex === bestRouteIndex) return;
    setSelectedRouteIndex(bestRouteIndex);
  }, [bestRouteIndex, quoteRoutes.length, selectedRouteIndex]);
  const selectedRoute = quoteRoutes.length > 0
    ? quoteRoutes[Math.min(selectedRouteIndex, quoteRoutes.length - 1)]
    : null;

  const selectedRouteGeometry = selectedRoute?.geometry || null;

  const displayedDistanceKm = selectedRoute?.distance_km ?? quote?.distance_km;
  const displayedDurationSeconds = selectedRoute?.duration_seconds ?? quote?.duration_seconds;
  const displayedTotalFires = selectedRoute?.gross_fare_fires ?? quote?.gross_fare_fires ?? quote?.fare_fires;

  let vesPerUsdt = null;
  if (fiatContext?.bcvRate && fiatContext.bcvRate.rate != null) {
    const bcvParsed = parseFloat(String(fiatContext.bcvRate.rate));
    if (Number.isFinite(bcvParsed) && bcvParsed > 0) {
      vesPerUsdt = bcvParsed;
    }
  }
  if (!vesPerUsdt && fiatContext?.operationalRate?.rate != null) {
    const opParsed = parseFloat(String(fiatContext.operationalRate.rate));
    if (Number.isFinite(opParsed) && opParsed > 0) {
      vesPerUsdt = opParsed;
    }
  }
  const firesPerUsdt = fiatContext?.config?.fires_per_usdt;
  const hasRates = Number.isFinite(Number(vesPerUsdt)) && Number(vesPerUsdt) > 0 && Number.isFinite(Number(firesPerUsdt)) && Number(firesPerUsdt) > 0;

  const displayedTotalUsdt = hasRates ? Number(displayedTotalFires || 0) / Number(firesPerUsdt) : null;
  const displayedTotalBs = hasRates && displayedTotalUsdt != null ? displayedTotalUsdt * Number(vesPerUsdt) : null;

  const fallbackFromCoords = fromCoords || extractCoordsFromText(fromLabel);
  const fallbackToCoords = toCoords || extractCoordsFromText(toLabel);

  const previewFromCoords = quoteHasFrom ? quoteFromCoords : fallbackFromCoords;
  const previewToCoords = quoteHasTo ? quoteToCoords : fallbackToCoords;
  const previewHasFrom = previewFromCoords && Number.isFinite(Number(previewFromCoords.lat)) && Number.isFinite(Number(previewFromCoords.lng));
  const previewHasTo = previewToCoords && Number.isFinite(Number(previewToCoords.lat)) && Number.isFinite(Number(previewToCoords.lng));
  const shouldShowPreviewMap = previewHasFrom || previewHasTo;

  const quoteDirectionsLink =
    quoteHasFrom && quoteHasTo
      ? `https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(
          `${quoteFromCoords.lat},${quoteFromCoords.lng}`
        )}&destination=${encodeURIComponent(`${quoteToCoords.lat},${quoteToCoords.lng}`)}&travelmode=driving`
      : null;

  const quoteDestinationLink = quoteHasTo
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${quoteToCoords.lat},${quoteToCoords.lng}`)}`
    : null;

  const googlePickerCenter = (() => {
    const c = googlePickerCoords || previewFromCoords || previewToCoords;
    return c && c.lat != null && c.lng != null ? [Number(c.lat), Number(c.lng)] : [10.4806, -66.9036];
  })();

  const createRide = useMutation({
    mutationFn: async (payload) => {
      const res = await axios.post('/api/transport/create', payload);
      return res.data;
    },
    onSuccess: () => {
      const nextFrom = String(fromLabel || '').trim();
      const nextTo = String(toLabel || '').trim();
      if (nextFrom) {
        setFromSuggestions((prev) => {
          const merged = [nextFrom, ...(Array.isArray(prev) ? prev : [])]
            .map((v) => String(v || '').trim())
            .filter(Boolean);
          const deduped = Array.from(new Set(merged)).slice(0, 10);
          writeStringSuggestions(MX_TRANSPORT_FROM_SUGGESTIONS_KEY, deduped);
          return deduped;
        });
      }
      if (nextTo) {
        setToSuggestions((prev) => {
          const merged = [nextTo, ...(Array.isArray(prev) ? prev : [])]
            .map((v) => String(v || '').trim())
            .filter(Boolean);
          const deduped = Array.from(new Set(merged)).slice(0, 10);
          writeStringSuggestions(MX_TRANSPORT_TO_SUGGESTIONS_KEY, deduped);
          return deduped;
        });
      }

      queryClient.invalidateQueries(['transport-requests']);
      queryClient.invalidateQueries(['transport-requests-ride-layout']);
      onCreated?.();
      setQuote(null);
      setSelectedRouteIndex(0);
      setNotes('');
      setFromLabel('');
      setToLabel('');
      setFromCoords(null);
      setToCoords(null);
      setPickMode(null);
      setGooglePickerOpen(false);
      setGooglePickerTarget(null);
      setGooglePickerText('');
      setGooglePickerError(null);
      setGooglePickerCoords(null);
      setError(null);

      setTravelMode('now');
      setScheduledDate('');
      setScheduledTime('');
    }
  });

  const buildScheduledAtIso = () => {
    if (travelMode !== 'later') return null;
    const d = String(scheduledDate || '').trim();
    const t = String(scheduledTime || '').trim();
    if (!d || !t) return null;
    // Interpret as local time and send as ISO.
    const dt = new Date(`${d}T${t}:00`);
    if (!Number.isFinite(dt.getTime())) return null;
    return dt.toISOString();
  };

  const openGooglePicker = (target) => {
    setGooglePickerError(null);
    setGooglePickerTarget(target);
    const seed = target === 'from' ? fromLabel : toLabel;
    const seedText = String(seed || '').trim();
    setGooglePickerText(seedText);
    const seedCoords = extractCoordsFromText(seedText);
    setGooglePickerCoords(seedCoords);
    setGooglePickerOpen(true);
  };

  const normalizeMapLink = async (url) => {
    const trimmed = String(url || '').trim();
    if (!trimmed) return null;
    if (!/^https?:\/\//i.test(trimmed)) return null;
    try {
      const res = await axios.post('/api/ubicacion/normalize-map-link', { url: trimmed });
      const normalizedUrl = res?.data?.url ? String(res.data.url) : trimmed;
      const coords = res?.data?.coords || null;
      return { url: normalizedUrl, coords };
    } catch {
      return { url: trimmed, coords: null };
    }
  };

  const scheduleResolveMainInput = (target, value) => {
    const trimmed = String(value || '').trim();
    const timerRef = target === 'from' ? fromResolveTimerRef : toResolveTimerRef;
    const resolvingRef = target === 'from' ? fromResolvingRef : toResolvingRef;
    const seqRef = target === 'from' ? fromResolveSeqRef : toResolveSeqRef;

    if (quoteLockedRef.current) return;

    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    if (!trimmed) return;
    if (resolvingRef.current) return;

    seqRef.current += 1;
    const mySeq = seqRef.current;

    timerRef.current = setTimeout(async () => {
      if (resolvingRef.current) return;
      resolvingRef.current = true;

      try {
        if (quoteLockedRef.current) return;
        if (seqRef.current !== mySeq) return;
        let working = trimmed;
        let coords = extractCoordsFromText(working);

        if (!coords && /^https?:\/\//i.test(working)) {
          const normalized = await normalizeMapLink(working);
          if (quoteLockedRef.current) return;
          if (seqRef.current !== mySeq) return;
          if (normalized?.url) {
            working = normalized.url;
            coords = normalized?.coords || extractCoordsFromText(working) || coords;
            if (!coords) {
              try {
                const u = new URL(working);
                const q = u.searchParams.get('q') || u.searchParams.get('query');
                if (q) coords = extractCoordsFromText(q) || coords;
              } catch {
              }
            }
          }
        }

        if (coords) {
          const place = await reverseGeocode(coords);
          if (quoteLockedRef.current) return;
          if (seqRef.current !== mySeq) return;
          const nextLabel = place || (target === 'from' ? 'Origen' : 'Destino');
          const prevCoords = target === 'from' ? fromCoordsRef.current : toCoordsRef.current;
          const didCoordsChange = !coordsEqual(prevCoords, coords);

          const hasQuoteNow = !!quoteRef.current;

          if (hasQuoteNow && didCoordsChange) {
            return;
          }

          if (target === 'from') {
            setFromCoords(coords);
            setFromLabel(nextLabel);
          } else {
            setToCoords(coords);
            setToLabel(nextLabel);
          }

          if (didCoordsChange && hasQuoteNow) {
            // do not clear quote from background resolvers
            return;
          }
        }
      } finally {
        resolvingRef.current = false;
      }
    }, 550);
  };

  const applyGooglePicker = async () => {
    setGooglePickerError(null);
    setGooglePickerResolving(false);
    const text = String(googlePickerText || '').trim();
    let coords = extractCoordsFromText(text) || googlePickerCoords;

    if (!coords && /^https?:\/\//i.test(text)) {
      setGooglePickerResolving(true);
      const normalized = await normalizeMapLink(text);
      if (normalized?.url) {
        coords = normalized?.coords || extractCoordsFromText(normalized.url) || coords;
        if (!coords) {
          try {
            const u = new URL(normalized.url);
            const q = u.searchParams.get('q') || u.searchParams.get('query');
            if (q) coords = extractCoordsFromText(q) || coords;
          } catch {
          }
        }
        if (coords) {
          setGooglePickerText(normalized.url);
        }
      }
      setGooglePickerResolving(false);
    }

    if (!coords) {
      setGooglePickerError('Pega un link de Google Maps (incluye @lat,lng) o escribe "lat,lng". Si es maps.app.goo.gl, espera a que se resuelva.');
      return;
    }
    if (googlePickerTarget === 'from') {
      const didCoordsChange = !coordsEqual(fromCoords, coords);
      setFromLabel(text);
      setFromCoords(coords);
      if (quote && didCoordsChange) {
        explicitClearRef.current = true;
        clearQuote('picker-from');
      }
    }
    if (googlePickerTarget === 'to') {
      const didCoordsChange = !coordsEqual(toCoords, coords);
      setToLabel(text);
      setToCoords(coords);
      if (quote && didCoordsChange) {
        explicitClearRef.current = true;
        clearQuote('picker-to');
      }
    }
    setPickMode(null);
    setGooglePickerOpen(false);
  };

  const canAutoQuote = previewHasFrom && previewHasTo;

  useEffect(() => {
    if (!canAutoQuote) return;
    if (quote) return;
    if (isQuoting) return;

    const currentKey = makeQuoteCacheKey(fromCoords, toCoords);
    if (currentKey && lastQuotedKeyRef.current === currentKey) {
      return;
    }

    if (autoQuoteTimerRef.current) {
      clearTimeout(autoQuoteTimerRef.current);
      autoQuoteTimerRef.current = null;
    }

    autoQuoteTimerRef.current = setTimeout(() => {
      handleQuote({ preventDefault: () => {} });
    }, 500);

    return () => {
      if (autoQuoteTimerRef.current) {
        clearTimeout(autoQuoteTimerRef.current);
        autoQuoteTimerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canAutoQuote, fromLabel, toLabel, fromCoords, toCoords]);

  const handleQuote = async (e) => {
    e.preventDefault();
    setError(null);
    try {
      if (fromResolvingRef.current || toResolvingRef.current) {
        setError('Espera un momento mientras se resuelve la ubicación...');
        return;
      }

      let nextFromLabel = fromLabel;
      let nextToLabel = toLabel;
      let nextFromCoords = fromCoords;
      let nextToCoords = toCoords;

      if (!nextFromCoords && /^https?:\/\//i.test(String(nextFromLabel || '').trim())) {
        const normalized = await normalizeMapLink(nextFromLabel);
        const normalizedUrl = normalized?.url || nextFromLabel;
        const normalizedCoords = normalized?.coords || extractCoordsFromText(normalizedUrl);
        if (normalizedCoords) {
          nextFromCoords = normalizedCoords;
          nextFromLabel = normalizedUrl;
          setFromCoords(normalizedCoords);
          setFromLabel(normalizedUrl);
        }
      }

      if (!nextToCoords && /^https?:\/\//i.test(String(nextToLabel || '').trim())) {
        const normalized = await normalizeMapLink(nextToLabel);
        const normalizedUrl = normalized?.url || nextToLabel;
        const normalizedCoords = normalized?.coords || extractCoordsFromText(normalizedUrl);
        if (normalizedCoords) {
          nextToCoords = normalizedCoords;
          nextToLabel = normalizedUrl;
          setToCoords(normalizedCoords);
          setToLabel(normalizedUrl);
        }
      }

      setIsQuoting(true);
      const res = await axios.post('/api/transport/quote', {
        kind: 'ride',
        from_location: buildLocationPayload(nextFromLabel, nextFromCoords),
        to_location: buildLocationPayload(nextToLabel, nextToCoords),
        vehicle_type: vehicleType,
        notes,
        max_alternatives: 3,
        store_id: storeId,
      });

      const nextQuote = res.data?.quote || res.data;
      setQuote(nextQuote);

      const routes = Array.isArray(nextQuote?.routes) ? nextQuote.routes : [];
      if (routes.length > 0) {
        let bestIdx = 0;
        let bestDistance = Number(routes[0]?.distance_km);
        if (!Number.isFinite(bestDistance)) bestDistance = Infinity;
        routes.forEach((r, idx) => {
          const d = Number(r?.distance_km);
          if (Number.isFinite(d) && d < bestDistance) {
            bestDistance = d;
            bestIdx = idx;
          }
        });
        setSelectedRouteIndex(bestIdx);

        const cacheKey = makeQuoteCacheKey(fromCoords, toCoords);
        if (cacheKey) {
          writeQuoteCache({
            key: cacheKey,
            ts: Date.now(),
            quote: nextQuote,
            selectedRouteIndex: bestIdx,
          });
        }
      } else {
        setSelectedRouteIndex(0);
        const cacheKey = makeQuoteCacheKey(fromCoords, toCoords);
        if (cacheKey) {
          writeQuoteCache({
            key: cacheKey,
            ts: Date.now(),
            quote: nextQuote,
            selectedRouteIndex: 0,
          });
        }
      }

      const nextFrom = String(fromLabel || '').trim();
      const nextTo = String(toLabel || '').trim();
      if (nextFrom) {
        setFromSuggestions((prev) => {
          const merged = [nextFrom, ...(Array.isArray(prev) ? prev : [])]
            .map((v) => String(v || '').trim())
            .filter(Boolean);
          const deduped = Array.from(new Set(merged)).slice(0, 10);
          writeStringSuggestions(MX_TRANSPORT_FROM_SUGGESTIONS_KEY, deduped);
          return deduped;
        });
      }
      if (nextTo) {
        setToSuggestions((prev) => {
          const merged = [nextTo, ...(Array.isArray(prev) ? prev : [])]
            .map((v) => String(v || '').trim())
            .filter(Boolean);
          const deduped = Array.from(new Set(merged)).slice(0, 10);
          writeStringSuggestions(MX_TRANSPORT_TO_SUGGESTIONS_KEY, deduped);
          return deduped;
        });
      }

    } catch (err) {
      console.error('RideCreateForm quote error', err);
      setError(getBackendErrorMessage(err, 'No se pudo cotizar'));
    } finally {
      setIsQuoting(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setError(null);
    if (!quote) {
      setError('Primero cotiza para poder crear la solicitud.');
      return;
    }
    try {
      const scheduledAtIso = buildScheduledAtIso();
      if (travelMode === 'later') {
        if (!scheduledAtIso) {
          setError('Selecciona fecha y hora para programar el viaje.');
          return;
        }
        if (new Date(scheduledAtIso).getTime() < Date.now() - 5 * 60 * 1000) {
          setError('La fecha/hora programada no puede estar en el pasado.');
          return;
        }
      }

      const payload = {
        kind: 'ride',
        source: 'manual',
        vehicle_type: vehicleType,
        from_location: buildLocationPayload(fromLabel, fromCoords),
        to_location: buildLocationPayload(toLabel, toCoords),
        notes: notes || null,
        route_index: selectedRouteIndex,
        max_alternatives: 3,
        scheduled_at: travelMode === 'later' ? scheduledAtIso : null,
        quote_snapshot: quote,
        store_id: storeId,
      };
      await createRide.mutateAsync(payload);
    } catch (err) {
      const status = err?.response?.status;
      const code = err?.response?.data?.code;
      if (status === 402 && code === 'INSUFFICIENT_FIRES') {
        const missing = Number(err?.response?.data?.missingFires);
        setMissingFires(Number.isFinite(missing) ? missing : 0);
        setShowInsufficientFires(true);
        return;
      }
      setError(getBackendErrorMessage(err, 'No se pudo crear la solicitud'));
    }
  };

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (fromCoords && Number.isFinite(Number(fromCoords.lat)) && Number.isFinite(Number(fromCoords.lng))) {
      try {
        window.localStorage.setItem(
          MX_TRANSPORT_LAST_FROM_KEY,
          JSON.stringify({ label: fromLabel || 'Origen', coords: { lat: fromCoords.lat, lng: fromCoords.lng } })
        );
      } catch {
      }
    }
  }, [fromCoords, fromLabel]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (toCoords && Number.isFinite(Number(toCoords.lat)) && Number.isFinite(Number(toCoords.lng))) {
      try {
        window.localStorage.setItem(
          MX_TRANSPORT_LAST_TO_KEY,
          JSON.stringify({ label: toLabel || 'Destino', coords: { lat: toCoords.lat, lng: toCoords.lng } })
        );
      } catch {
      }
    }
  }, [toCoords, toLabel]);

  const renderPickStatus = () => {
    if (!pickMode) return null;
    const label = pickMode === 'from' ? 'Origen' : 'Destino';
    return (
      <div className="text-[11px] text-slate-300/80">
        Selecciona en el mapa: <span className="font-semibold">{label}</span>
      </div>
    );
  };

  const showMapPicker = shouldShowPreviewMap;

  return (
    <>
      <SectionCard title={title}>
        <div className="space-y-3">
        <div className="grid gap-2">
          {isLocatingOrigin ? (
            <div className="rounded-lg border border-blue-500/30 bg-blue-500/10 px-3 py-2 text-[11px] text-blue-100">
              Estamos detectando tu ubicación actual para usarla como origen...
            </div>
          ) : null}

          {originLocateError ? (
            <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-[11px] text-amber-100">
              {originLocateError}
            </div>
          ) : null}

          <div className="text-[11px] text-slate-400">Horario</div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setTravelMode('now')}
              className={`flex-1 px-3 py-2 rounded-lg text-xs font-semibold border transition ${
                travelMode === 'now'
                  ? 'bg-blue-600/30 text-blue-100 border-blue-500/50'
                  : 'bg-slate-900 text-slate-200 border-slate-800 hover:border-slate-700'
              }`}
            >
              Viajar ya
            </button>
            <button
              type="button"
              onClick={() => setTravelMode('later')}
              className={`flex-1 px-3 py-2 rounded-lg text-xs font-semibold border transition ${
                travelMode === 'later'
                  ? 'bg-orange-500/20 text-orange-100 border-orange-400/40'
                  : 'bg-slate-900 text-slate-200 border-slate-800 hover:border-slate-700'
              }`}
            >
              Viajar en otro momento
            </button>
          </div>

          {invoiceOpen ? (
            <div className="fixed inset-0 z-[1800] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
              <div className="bg-[#0b1220] border border-slate-800 rounded-2xl w-full max-w-md shadow-2xl shadow-black/50 overflow-hidden">
                <div className="flex items-center justify-between px-4 py-3 border-b border-slate-800">
                  <div className="text-slate-100 font-semibold">Factura de pago</div>
                  <button
                    type="button"
                    onClick={() => {
                      setInvoiceOpen(false);
                      setInvoiceQrOpen(false);
                    }}
                    className="text-slate-400 hover:text-white text-sm px-2 py-1 rounded"
                  >
                    Cerrar
                  </button>
                </div>

                <div className="p-4 space-y-3">
                  {invoiceLoading ? <div className="text-sm text-slate-300">Generando...</div> : null}
                  {invoiceError ? <div className="text-sm text-red-400">{invoiceError}</div> : null}

                  {invoiceData ? (
                    <>
                      <div className="text-xs text-slate-400">Link</div>
                      <div className="break-all text-[11px] text-slate-200 bg-slate-900 border border-slate-800 rounded-lg p-2">
                        {invoiceData.url}
                      </div>

                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={() => copyInvoiceLink(invoiceData.url)}
                          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-800 text-slate-100 border border-slate-700 text-xs font-semibold hover:border-blue-500/60"
                        >
                          Copiar link
                        </button>
                        <button
                          type="button"
                          onClick={() => shareInvoiceLink(invoiceData.url)}
                          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-800 text-slate-100 border border-slate-700 text-xs font-semibold hover:border-blue-500/60"
                        >
                          Compartir
                        </button>
                      </div>

                      {invoiceData.qrDataUrl ? (
                        <div className="flex items-center justify-center bg-white rounded-xl p-3">
                          <button type="button" onClick={() => setInvoiceQrOpen(true)} className="block" title="Ampliar QR">
                            <img src={invoiceData.qrDataUrl} alt="QR" className="w-56 h-56" />
                          </button>
                        </div>
                      ) : null}

                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            try {
                              window.open(invoiceData.url, '_blank', 'noopener,noreferrer');
                            } catch {
                            }
                          }}
                          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-blue-600 text-white text-xs font-semibold hover:bg-blue-500"
                        >
                          Abrir link
                        </button>
                        <button
                          type="button"
                          onClick={() => downloadQrForUrl(invoiceData.url, `transport-qr-${invoiceData.sessionId}.png`)}
                          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-800 text-slate-100 border border-slate-700 text-xs font-semibold hover:border-blue-500/60"
                        >
                          Descargar QR
                        </button>
                      </div>
                    </>
                  ) : null}

                  {!invoiceData ? (
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        disabled={!quote || invoiceLoading}
                        onClick={() => generateInvoiceFromQuote({ resQuote: quote, routeIndexOverride: selectedRouteIndex })}
                        className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold transition border ${
                          !quote || invoiceLoading
                            ? 'bg-slate-900 text-slate-500 border-slate-800 cursor-not-allowed'
                            : 'bg-blue-600 text-white border-blue-500/40 hover:bg-blue-500'
                        }`}
                      >
                        Generar factura
                      </button>
                      <button
                        type="button"
                        onClick={() => setInvoiceError(null)}
                        className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-800 text-slate-100 border border-slate-700 text-xs font-semibold hover:border-blue-500/60"
                      >
                        Limpiar error
                      </button>
                    </div>
                  ) : null}

                  {!invoiceLoading && !invoiceError && !invoiceData ? (
                    <div className="text-xs text-slate-400">Genera una factura para que el cliente pague con Fires.</div>
                  ) : null}
                </div>
              </div>
            </div>
          ) : null}

          {invoiceOpen && invoiceData?.qrDataUrl && invoiceQrOpen ? (
            <div
              className="fixed inset-0 z-[1900] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
              onClick={() => setInvoiceQrOpen(false)}
            >
              <div className="bg-white rounded-2xl p-4" onClick={(e) => e.stopPropagation()}>
                <img src={invoiceData.qrDataUrl} alt="QR" className="w-[320px] h-[320px]" />
                <button
                  type="button"
                  onClick={() => setInvoiceQrOpen(false)}
                  className="mt-3 w-full py-2 rounded-xl bg-slate-900 text-white text-xs font-semibold"
                >
                  Cerrar
                </button>
              </div>
            </div>
          ) : null}

          {travelMode === 'later' ? (
            <div className="grid grid-cols-2 gap-2">
              <input
                type="date"
                value={scheduledDate}
                onChange={(e) => setScheduledDate(e.target.value)}
                className="w-full bg-slate-950 border border-slate-800 rounded-lg px-3 py-2 text-[12px] md:text-sm text-slate-100"
              />
              <input
                type="time"
                value={scheduledTime}
                onChange={(e) => setScheduledTime(e.target.value)}
                className="w-full bg-slate-950 border border-slate-800 rounded-lg px-3 py-2 text-[12px] md:text-sm text-slate-100"
              />
            </div>
          ) : null}
        </div>

        <form className={compact ? 'space-y-2' : 'space-y-3'} onSubmit={handleCreate}>
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-400">Origen</label>
              {(fromSuggestions || []).length ? (
                <div className="mt-1 space-y-1">
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-[11px] text-slate-400">Últimos orígenes</div>
                    <button
                      type="button"
                      onClick={() => clearStoredSuggestions('from')}
                      className="text-[11px] px-2 py-1 rounded-md border border-slate-700 text-slate-300 hover:border-rose-500/60 hover:text-rose-200"
                    >
                      Limpiar historial
                    </button>
                  </div>
                  <select
                    className="w-full bg-slate-900 text-slate-100 border border-slate-800 rounded-lg px-3 py-2 text-sm"
                    value={recentFromSelection}
                    onChange={(e) => {
                      const selected = e.target.value;
                      setRecentFromSelection(selected);
                      if (!selected) return;
                      fromTouchedRef.current = true;
                      quoteLockedRef.current = false;
                      setFromLabel(selected);
                      const parsed = extractCoordsFromText(selected);
                      if (parsed) setFromCoords(parsed);
                      const didCoordsChange = parsed ? !coordsEqual(fromCoords, parsed) : false;
                      if (quote && didCoordsChange) {
                        explicitClearRef.current = true;
                        clearQuote('recent-from');
                      }
                      setTimeout(() => setRecentFromSelection(''), 0);
                    }}
                  >
                    <option value="">Selecciona un origen reciente...</option>
                    {(fromSuggestions || []).map((sug) => (
                      <option key={sug} value={sug}>
                        {sug}
                      </option>
                    ))}
                  </select>
                </div>
              ) : null}
              <div className="relative mt-1">
                <input
                  list="mx-transport-from-suggestions"
                  className="w-full bg-slate-900 text-slate-100 border border-slate-800 rounded-lg px-3 py-2 text-sm"
                  placeholder="Busca una dirección, avenida o lugar"
                  value={fromLabel}
                  onFocus={() => {
                    if (decoratedFromSearchResults.length > 0) setFromSearchOpen(true);
                  }}
                  onBlur={() => {
                    setTimeout(() => setFromSearchOpen(false), 140);
                  }}
                  onChange={(e) => {
                    fromTouchedRef.current = true;
                    quoteLockedRef.current = false;
                    const next = e.target.value;
                    setFromLabel(next);
                    const parsed = extractCoordsFromText(next);
                    if (parsed) {
                      const didCoordsChange = !coordsEqual(fromCoords, parsed);
                      setFromCoords(parsed);
                      clearLocationSearch('from');
                      if (quote && didCoordsChange) {
                        explicitClearRef.current = true;
                        clearQuote('input-from');
                      }
                    } else {
                      const hadCoords = !!fromCoords;
                      if (hadCoords) {
                        setFromCoords(null);
                        if (quote) {
                          explicitClearRef.current = true;
                          clearQuote('input-from-clear');
                        }
                      }
                      scheduleLocationSearch('from', next);
                    }
                    scheduleResolveMainInput('from', next);
                  }}
                  required
                />
                {fromSearchOpen ? (
                  <div className="absolute left-0 right-0 top-[calc(100%+0.35rem)] z-20 rounded-xl border border-slate-700 bg-[#0b1220] shadow-2xl shadow-black/40 overflow-hidden">
                    {fromSearchLoading ? (
                      <div className="px-3 py-2 text-[11px] text-slate-300/80">Buscando ubicaciones...</div>
                    ) : decoratedFromSearchResults.length > 0 ? (
                      <div className="max-h-64 overflow-y-auto">
                        {decoratedFromSearchResults.map((result) => (
                          <button
                            key={result.id}
                            type="button"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => selectLocationSearchResult('from', result)}
                            className={`w-full text-left px-3 py-2 border-b border-slate-800 last:border-b-0 hover:bg-slate-800/70 transition-colors ${result.isCurrentLocation ? 'bg-blue-500/10' : ''}`}
                          >
                            <div className="flex items-start gap-3">
                              <div className="text-lg leading-none pt-0.5">{getPlaceTypeIcon(result)}</div>
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2">
                                  <div className="text-sm text-slate-100 truncate">{result.shortLabel}</div>
                                  <span className={`shrink-0 rounded-full border px-2 py-0.5 text-[10px] ${result.isCurrentLocation ? 'border-blue-400/40 text-blue-200 bg-blue-500/10' : 'border-slate-700 text-slate-300'}`}>
                                    {result.isCurrentLocation ? 'Actual' : normalizePlaceTypeLabel(result)}
                                  </span>
                                </div>
                                <div className="text-[11px] text-slate-400 truncate">{result.label}</div>
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                    ) : (
                      <div className="px-3 py-2 text-[11px] text-slate-400">Sin resultados todavía.</div>
                    )}
                  </div>
                ) : null}
              </div>
              <datalist id="mx-transport-from-suggestions">
                {(fromSuggestions || []).map((sug) => (
                  <option key={sug} value={sug} />
                ))}
              </datalist>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setSavedLocationsTarget('from');
                    setSavedLocationsOpen(true);
                  }}
                  className="px-3 py-2 rounded-lg text-sm border bg-slate-800 border-slate-700 text-slate-100 hover:border-blue-500/60 disabled:opacity-60"
                >
                  Mis direcciones
                </button>
                <button
                  type="button"
                  onClick={() => openGooglePicker('from')}
                  className={`px-3 py-2 rounded-lg text-sm border transition ${
                    pickMode === 'from'
                      ? 'bg-blue-600/20 border-blue-500/50 text-blue-100'
                      : 'bg-slate-800 border-slate-700 text-slate-100 hover:border-blue-500/60'
                  }`}
                >
                  Seleccionar origen en mapa
                </button>
              </div>
            </div>
            <div>
              <label className="text-xs text-slate-400">Destino</label>
              {(toSuggestions || []).length ? (
                <div className="mt-1 space-y-1">
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-[11px] text-slate-400">Últimos destinos</div>
                    <button
                      type="button"
                      onClick={() => clearStoredSuggestions('to')}
                      className="text-[11px] px-2 py-1 rounded-md border border-slate-700 text-slate-300 hover:border-rose-500/60 hover:text-rose-200"
                    >
                      Limpiar historial
                    </button>
                  </div>
                  <select
                    className="w-full bg-slate-900 text-slate-100 border border-slate-800 rounded-lg px-3 py-2 text-sm"
                    value={recentToSelection}
                    onChange={(e) => {
                      quoteLockedRef.current = false;
                      const selected = e.target.value;
                      setRecentToSelection(selected);
                      if (!selected) return;
                      setToLabel(selected);
                      const parsed = extractCoordsFromText(selected);
                      if (parsed) setToCoords(parsed);
                      const didCoordsChange = parsed ? !coordsEqual(toCoords, parsed) : false;
                      if (quote && didCoordsChange) {
                        explicitClearRef.current = true;
                        clearQuote('recent-to');
                      }
                      setTimeout(() => setRecentToSelection(''), 0);
                    }}
                  >
                    <option value="">Selecciona un destino reciente...</option>
                    {(toSuggestions || []).map((sug) => (
                      <option key={sug} value={sug}>
                        {sug}
                      </option>
                    ))}
                  </select>
                </div>
              ) : null}
              <div className="relative mt-1">
                <input
                  list="mx-transport-to-suggestions"
                  className="w-full bg-slate-900 text-slate-100 border border-slate-800 rounded-lg px-3 py-2 text-sm"
                  placeholder="Busca el destino exacto"
                  value={toLabel}
                  onFocus={() => {
                    if (toSearchResults.length > 0) setToSearchOpen(true);
                  }}
                  onBlur={() => {
                    setTimeout(() => setToSearchOpen(false), 140);
                  }}
                  onChange={(e) => {
                    quoteLockedRef.current = false;
                    const next = e.target.value;
                    setToLabel(next);
                    const parsed = extractCoordsFromText(next);
                    if (parsed) {
                      const didCoordsChange = !coordsEqual(toCoords, parsed);
                      setToCoords(parsed);
                      clearLocationSearch('to');
                      if (quote && didCoordsChange) {
                        explicitClearRef.current = true;
                        clearQuote('input-to');
                      }
                    } else {
                      const hadCoords = !!toCoords;
                      if (hadCoords) {
                        setToCoords(null);
                        if (quote) {
                          explicitClearRef.current = true;
                          clearQuote('input-to-clear');
                        }
                      }
                      scheduleLocationSearch('to', next);
                    }
                    scheduleResolveMainInput('to', next);
                  }}
                  required
                />
                {toSearchOpen ? (
                  <div className="absolute left-0 right-0 top-[calc(100%+0.35rem)] z-20 rounded-xl border border-slate-700 bg-[#0b1220] shadow-2xl shadow-black/40 overflow-hidden">
                    {toSearchLoading ? (
                      <div className="px-3 py-2 text-[11px] text-slate-300/80">Buscando ubicaciones...</div>
                    ) : toSearchResults.length > 0 ? (
                      <div className="max-h-64 overflow-y-auto">
                        {toSearchResults.map((result) => (
                          <button
                            key={result.id}
                            type="button"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => selectLocationSearchResult('to', result)}
                            className="w-full text-left px-3 py-2 border-b border-slate-800 last:border-b-0 hover:bg-slate-800/70 transition-colors"
                          >
                            <div className="flex items-start gap-3">
                              <div className="text-lg leading-none pt-0.5">{getPlaceTypeIcon(result)}</div>
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2">
                                  <div className="text-sm text-slate-100 truncate">{result.shortLabel}</div>
                                  <span className="shrink-0 rounded-full border border-slate-700 px-2 py-0.5 text-[10px] text-slate-300">
                                    {normalizePlaceTypeLabel(result)}
                                  </span>
                                </div>
                                <div className="text-[11px] text-slate-400 truncate">{result.label}</div>
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                    ) : (
                      <div className="px-3 py-2 text-[11px] text-slate-400">Sin resultados todavía.</div>
                    )}
                  </div>
                ) : null}
              </div>
              <datalist id="mx-transport-to-suggestions">
                {(toSuggestions || []).map((sug) => (
                  <option key={sug} value={sug} />
                ))}
              </datalist>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setSavedLocationsTarget('to');
                    setSavedLocationsOpen(true);
                  }}
                  className="px-3 py-2 rounded-lg text-sm border bg-slate-800 border-slate-700 text-slate-100 hover:border-blue-500/60 disabled:opacity-60"
                >
                  Mis direcciones
                </button>
                <button
                  type="button"
                  onClick={() => openGooglePicker('to')}
                  className={`px-3 py-2 rounded-lg text-sm border transition ${
                    pickMode === 'to'
                      ? 'bg-blue-600/20 border-blue-500/50 text-blue-100'
                      : 'bg-slate-800 border-slate-700 text-slate-100 hover:border-blue-500/60'
                  }`}
                >
                  Seleccionar destino en mapa
                </button>
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-400">Vehículo</label>
              <select
                className="w-full mt-1 bg-slate-900 text-slate-100 border border-slate-800 rounded-lg px-3 py-2 text-sm"
                value={vehicleType}
                onChange={(e) => setVehicleType(e.target.value)}
              >
                <option value="moto">Moto</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-slate-400">Notas</label>
              <input
                className="w-full mt-1 bg-slate-900 text-slate-100 border border-slate-800 rounded-lg px-3 py-2 text-sm"
                placeholder="Indicaciones, referencia..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={handleQuote}
              disabled={isQuoting}
              className="px-4 py-2 bg-slate-800 border border-slate-700 text-slate-100 rounded-lg text-sm hover:border-blue-500/60 disabled:opacity-60"
            >
              {isQuoting ? 'Cotizando...' : 'Cotizar'}
            </button>
            {quote ? (
              <button
                type="submit"
                disabled={createRide.isLoading}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-500 disabled:opacity-60"
              >
                {createRide.isLoading ? 'Creando...' : 'Crear solicitud'}
              </button>
            ) : null}
          </div>

          {renderPickStatus()}

          {googlePickerOpen ? (
            <div className="fixed inset-0 z-[1600] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
              <div className="bg-[#0b1220] border border-slate-800 rounded-2xl w-full max-w-3xl shadow-2xl shadow-black/50 overflow-hidden">
                <div className="flex items-center justify-between px-4 py-3 border-b border-slate-800">
                  <div className="text-slate-100 font-semibold">
                    {googlePickerTarget === 'from' ? 'Seleccionar Origen' : 'Seleccionar Destino'}
                  </div>
                  <button
                    type="button"
                    onClick={() => setGooglePickerOpen(false)}
                    className="text-slate-400 hover:text-white text-sm px-2 py-1 rounded"
                  >
                    Cerrar
                  </button>
                </div>
                <div className="p-3 space-y-3">
                  <div className="text-[11px] text-slate-300/80">
                    Toca una vez el mapa para colocar el pin. También puedes pegar un link de Google Maps (o escribir "lat,lng").
                  </div>
                  <div className="h-64 w-full overflow-hidden rounded-lg border border-slate-800">
                    <TransportMapLibre
                      view="destination"
                      pickEnabled={true}
                      pickedCoords={googlePickerCoords}
                      onPick={(coords) => {
                        if (!coords) return;
                        setGooglePickerCoords(coords);
                        setGooglePickerText(`${coords.lat},${coords.lng}`);
                        setGooglePickerError(null);
                      }}
                    />
                  </div>
                  <input
                    className="w-full bg-slate-900 text-slate-100 border border-slate-800 rounded-lg px-3 py-2 text-sm"
                    placeholder='Pega link de Google Maps o "lat,lng"'
                    value={googlePickerText}
                    onChange={(e) => {
                      const next = e.target.value;
                      setGooglePickerText(next);
                      const parsed = extractCoordsFromText(next);
                      if (parsed) setGooglePickerCoords(parsed);
                    }}
                  />
                  {googlePickerResolving ? (
                    <div className="text-[11px] text-slate-300/80">Resolviendo link corto...</div>
                  ) : null}
                  {googlePickerError ? <div className="text-sm text-red-400">{googlePickerError}</div> : null}
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={applyGooglePicker}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-500"
                    >
                      Aplicar
                    </button>
                    <button
                      type="button"
                      onClick={() => setGooglePickerOpen(false)}
                      className="px-4 py-2 bg-slate-800 border border-slate-700 text-slate-100 rounded-lg text-sm hover:border-blue-500/60"
                    >
                      Cancelar
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : null}

          {quote ? (
            <div className={compact ? 'space-y-2' : 'space-y-2'}>
              <div className="text-sm text-slate-200 bg-slate-900 border border-slate-800 rounded-lg p-3">
                <div className="font-semibold text-slate-100">Cotización</div>
                <div>Distancia: {Number(displayedDistanceKm || 0).toFixed(3)} km</div>
                <div>
                  Duración:{' '}
                  {displayedDurationSeconds ? Math.round(Number(displayedDurationSeconds) / 60) + ' min' : 'N/D'}
                </div>
                <div className="font-semibold mt-1">{Number(displayedTotalFires || 0).toFixed(2)} 🔥</div>
                {displayedTotalUsdt != null && displayedTotalBs != null ? (
                  <div className="mt-1 grid gap-0.5 text-[11px] text-slate-300/80">
                    <div>Equivalente: {Number(displayedTotalUsdt || 0).toFixed(2)} USD (BCV)</div>
                    <div>Equivalente: {Number(displayedTotalBs || 0).toFixed(2)} Bs</div>
                  </div>
                ) : null}
              </div>

              {quoteRoutes.length > 0 ? (
                <div className="bg-slate-900 border border-slate-800 rounded-lg p-2">
                  {(() => {
                    const r = quoteRoutes[Math.min(bestRouteIndex, quoteRoutes.length - 1)];
                    const routeFires = Number(r?.gross_fare_fires || 0);
                    const routeUsdt = hasRates ? routeFires / Number(firesPerUsdt) : null;
                    const routeBs = hasRates && routeUsdt != null ? routeUsdt * Number(vesPerUsdt) : null;
                    return (
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <div className="text-[11px] text-slate-300/70">Ruta (más corta)</div>
                          <div className="mt-1 font-mono text-[11px] text-slate-100">{routeFires.toFixed(2)} 🔥</div>
                          {routeUsdt != null && routeBs != null ? (
                            <div className="mt-0.5 text-[10px] text-slate-300/70">
                              {Number(routeUsdt || 0).toFixed(2)} USD · {Number(routeBs || 0).toFixed(2)} Bs
                            </div>
                          ) : null}
                        </div>
                        <div className="font-mono text-[11px] text-slate-200">
                          {Number(r?.distance_km || 0).toFixed(3)} km
                        </div>
                      </div>
                    );
                  })()}
                </div>
              ) : null}
            </div>
          ) : null}

          {showMapPicker ? (
            <div className="bg-slate-900 border border-slate-800 rounded-lg p-2">
              <div className="flex items-center justify-between gap-2 px-1 pb-2">
                <div className="text-[11px] text-slate-300/70">
                  {previewHasFrom && previewHasTo ? 'Mapa / Ruta' : 'Mapa / Ubicación actual'}
                </div>
                <div className="flex items-center gap-3 text-[11px]">
                  {quoteDirectionsLink ? (
                    <a href={quoteDirectionsLink} target="_blank" rel="noreferrer" className="text-blue-300 underline">
                      Abrir ruta
                    </a>
                  ) : null}
                  {quoteDestinationLink ? (
                    <a href={quoteDestinationLink} target="_blank" rel="noreferrer" className="text-blue-300 underline">
                      Abrir destino
                    </a>
                  ) : null}
                </div>
              </div>
              <div className="h-72 w-full overflow-hidden rounded-lg border border-slate-800">
                <TransportMapLibre
                  view={quoteHasFrom && quoteHasTo ? 'route' : 'destination'}
                  fromCoords={quoteHasFrom ? quoteFromCoords : previewHasFrom ? previewFromCoords : null}
                  toCoords={quoteHasTo ? quoteToCoords : previewHasTo ? previewToCoords : null}
                  routeGeometry={selectedRouteGeometry}
                />
              </div>
            </div>
          ) : null}

          {error ? <div className="text-sm text-red-400">{error}</div> : null}
          </form>
        </div>
      </SectionCard>

      <InsufficientFiresModal
        isOpen={showInsufficientFires}
        onClose={() => setShowInsufficientFires(false)}
        missingFires={missingFires}
      />

      <TransportSavedLocationsModal
        isOpen={savedLocationsOpen}
        onClose={() => setSavedLocationsOpen(false)}
        onSelect={({ location }) => {
          const coords = location?.coords;
          const label = location?.label || location?.address || '';
          if (savedLocationsTarget === 'to') {
            applyToLocation({ label, coords });
          } else {
            applyFromLocation({ label, coords });
          }
          setSavedLocationsOpen(false);
        }}
        title={savedLocationsTarget === 'to' ? 'Mis direcciones (Destino)' : 'Mis direcciones (Origen)'}
      />
    </>
  );
}
